import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as moment from "moment";
import * as enums from "../../enumerations";
import * as modelsGet from "../../models/get";
import * as modelsPost from "../../models/post";
import * as modelsCalendar from "../../models/calendar";
import * as modelsForm from "../../models/form";
import * as utils from "../../utils/utils";
import * as utilsCalendar from "../../utils/calendar";
import * as globals from "../../globals";
import * as apiCalendars from "../../api/calendars";
import * as apiTimes from "../../api/times";
import { Window } from "../window";
import { Loader, LoaderStyle } from "../loader";
import { Button, ButtonType } from "../button";
import { ScrollViewer, ScrollBarVisibility } from "../scrollViewer";
import { FormGroup } from "../form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../comboBox";
import { InputMask, InputSize } from "../input";
import { TextBox } from "../textBox";
import { Alert, AlertType } from "../alert";
import { TextArea } from "../textArea";
import { Time } from "../../types/time";
import { DeleteDialog } from "./deleteDialog";
import { DateTextBox } from "../dateTextBox";
import { CheckBox } from "../checkbox";
import { ISearch } from "../../models/post";
import { GroupBox } from "../groupBox";
import { PopupAlign } from "../popup";
import { RadioButton } from "../radiobutton";

enum Mode
{
    create,
    edit
}

interface IInitOptions
{
    props: IProperties,
    blockId?: number,
    calendarGroups: modelsGet.ICalendarGroup[],
    calendars: modelsGet.ICalendar[]
}

interface ISeriesPattern
{
    id: number,
    pattern: string
}

interface IProperties
{
    /**
     * Die vorherige oder momentane Route. Wenn der Dialog als Modal geöffnet wird,
     * ist hier die vorherige Route drin.
     */
    route: () => uri.URI,

    /**
     * Die momentane Route. Wenn der Dialog als Modal geöffnet wird,
     * ist hier die eigentliche Route für den Termin Dialog drin.
     */
    currentRoute: () => uri.URI,

    /**
     * Das react-router history object.
     */
    history: any,

    /**
     * Die ID der Sperre die geöffnet werden soll. (Ist leer wenn ein neuer Termin erstellt werden soll.)
     */
    blockId?: number,

    /**
     * Angabe, ob eine Sperrserie erstellt werden soll.
     */
    isSeries: boolean,

    /**
     * Der selektierte Kalender oder die Kalendergruppe.
     */
    selectedObject: modelsCalendar.ISelectedObjectId,

    /**
     * Selektierte Datum.
     */
    selectedDate: moment.Moment,

    /**
     * Der selektierte Kalender (nur wenn eine Kalendergruppe ausgewählt ist).
     */
    selectedCalendar: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Enthält den momentan selektierten Kalender.
     */
    selectedObject?: modelsCalendar.ISelectedObject,

    /**
     * Der selektierte Kalender (nur wenn eine Kalendergruppe ausgewählt ist).
     */
    selectedCalendar?: modelsGet.ICalendar,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    block?: modelsGet.ITimeLock,

    /**
     * Alle eingerichteten Kalendergruppen.
     */
    calendarGroups?: modelsGet.ICalendarGroup[],

    /**
     * Alle eingerichteten Kalender.
     */
    calendars?: modelsGet.ICalendar[],

    /**
     * Alle möglichen Serienmuster.
     */
    seriesPatterns?: ISeriesPattern[],

    /**
     * Enthält alle Eigenschaften des Forms.
     */
    form?: {
        calendarIds?: string[],
        calendar?: modelsForm.IComboBoxValue<string, number>,
        startdate?: moment.Moment,
        enddate?: moment.Moment,
        timeFrom?: string,
        timeTo?: string,
        description?: string,
        isAllDay?: boolean,

        // Serienmuster
        seriesPattern?: modelsForm.IComboBoxValue<string, number>
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class LockDialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see LockDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.blockId > 0)
        {
            mode = Mode.edit;
        }

        // Serienmuster erstellen.
        var seriesPatterns: ISeriesPattern[] = [
            {
                id: 1,
                pattern: "Täglich"
            },
            {
                id: 2,
                pattern: "Wöchentlich"
            },
            {
                id: 3,
                pattern: "Monatlich"
            },
            {
                id: 4,
                pattern: "Jährlich"
            }
        ];

        this.state = {
            isLoading: true,
            mode: mode,
            seriesPatterns: seriesPatterns,
            form: {
                calendarIds: [],
                seriesPattern: {}
            },
            errors: {}
        };
    }

    /**
     * Richtet diese Komponente erstmalig ein.
     */
    async componentWillMount()
    {
        var calendarGroups: modelsGet.ICalendarGroup[] = await globals.getCalendarGroupsAsync();
        var calendars: modelsGet.ICalendar[] = await globals.getCalendarsAsync();
        var options: IInitOptions = {
            props: this.props,
            blockId: this.props.blockId,
            calendarGroups: calendarGroups,
            calendars: calendars
        };

        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(options);
        }
        else if (this.state.mode === Mode.create)
        {
            this.initCreateMode(options);
        }
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern eines Termins
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const {
                mode,
                form
            } = this._initialState;

            // Kalender Auswahl prüfen.
            if (mode === Mode.create)
            {
                if (form.calendarIds.length !== nextState.form.calendarIds.length)
                {
                    this._isSaveButtonDisabled = false;
                }
                else if ((form.calendarIds.length > 0 && nextState.form.calendarIds.length > 0) && (!form.calendarIds.any(l => nextState.form.calendarIds.any(l2 => l2 === l))))
                {
                    this._isSaveButtonDisabled = false;
                }
            }
            else
            {
                if (form.calendar.selectedValue !== nextState.form.calendar.selectedValue)
                    this._isSaveButtonDisabled = false;
            }

            if (form.startdate !== nextState.form.startdate)
                this._isSaveButtonDisabled = false;

            if (form.enddate !== nextState.form.enddate)
                this._isSaveButtonDisabled = false;

            if (form.timeFrom !== nextState.form.timeFrom)
                this._isSaveButtonDisabled = false;

            if (form.timeTo !== nextState.form.timeTo)
                this._isSaveButtonDisabled = false;

            if (form.description !== nextState.form.description)
                this._isSaveButtonDisabled = false;

            if (form.isAllDay !== nextState.form.isAllDay)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Initialisiert den Edit Mode.
     */
    private async initEditModeAsync(options: IInitOptions)
    {
        const { props, blockId } = options;

        // Sperre aus der Datenbank laden.
        var block: modelsGet.ITimeLock = await apiTimes.getLock(blockId);

        // Momentan selektierten Kalender (Wenn eine Gruppe ausgewählt ist).
        var selectedCalendar: modelsGet.ICalendar = null;
        if (props.selectedCalendar != null && props.selectedCalendar > 0)
            selectedCalendar = options.calendars.where(c => c.id === props.selectedCalendar).first();

        // Momentan selektierten Kalender oder Kalendergruppe ermitteln.
        var selectedObject: modelsCalendar.ISelectedObject;
        var promise = utilsCalendar.getSelectedObject(props.selectedObject, options.calendarGroups, options.calendars);
        promise.calendar((calendar) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: calendar
            };

            selectedCalendar = calendar;
        });
        promise.calendarGroup((group) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: group
            };
        });

        // Zur verfügung stehende Zeiten ermitteln.
        var startdate: moment.Moment = moment(block.starttime);
        var enddate: moment.Moment = moment(block.endtime);
        var repeatUntil: moment.Moment = block.repeat_until ? moment(block.repeat_until) : null;

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.selectedObject = selectedObject;
            newState.selectedCalendar = selectedCalendar;
            newState.block = block;
            newState.calendarGroups = options.calendarGroups;
            newState.calendars = options.calendars;

            newState.form = {
                calendar: {
                    value: block.calendar_name,
                    selectedValue: block.calendar_id
                },
                startdate: startdate,
                enddate: repeatUntil,
                timeFrom: !block.is_all_day && startdate.format("HH:mm"),
                timeTo: !block.is_all_day && enddate.format("HH:mm"),
                description: block.description,
                isAllDay: block.is_all_day
            };

            startdate.startOf("day");
            enddate.startOf("day");

            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisiert den Create Mode.
     */
    private initCreateMode(options: IInitOptions)
    {
        const { props } = options;

        // Momentan selektierten Kalender ermitteln (Wenn eine Gruppe ausgewählt ist).
        var uri: uri.URI = this.props.currentRoute();
        var calendarId: number = uri.getQueryParameterValue<number>("calendar");
        var selectedCalendar: modelsGet.ICalendar = null;

        if (calendarId > 0)
        {
            selectedCalendar = options.calendars.where(c => c.id === calendarId).first();
        }
        else
        {
            if (props.selectedCalendar != null && props.selectedCalendar > 0)
                selectedCalendar = options.calendars.where(c => c.id === props.selectedCalendar).first();
        }

        var selectedObject: modelsCalendar.ISelectedObject;
        var promise = utilsCalendar.getSelectedObject(props.selectedObject, options.calendarGroups, options.calendars);
        promise.calendar((calendar) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: calendar
            };

            if (!(calendarId > 0))
                selectedCalendar = calendar;
        });
        promise.calendarGroup((group) =>
        {
            selectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: group
            };
        });

        // Zur verfügung stehende Zeiten ermitteln.
        var prevUri: uri.URI = this.props.route();
        var uri: uri.URI = this.props.currentRoute();
        var viewMode: enums.CalendarViewMode = prevUri.getViewMode();
        var startdate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("date");
        var enddate: moment.Moment = uri.getQueryParameterValue<moment.Moment>("enddate");
        var timeFrom: Time = Time.parse(startdate.format("HH:mm"));

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.selectedObject = selectedObject;
            newState.selectedCalendar = selectedCalendar;
            newState.calendarGroups = options.calendarGroups;
            newState.calendars = options.calendars;

            // Form Daten leeren.
            newState.form = {
                calendarIds: [],
                seriesPattern: {}
            };

            newState.form.calendarIds = [selectedCalendar.id.toString()];
            newState.form.startdate = startdate;
            newState.form.timeFrom = timeFrom.isEmpty() ? null : startdate.format("HH:mm");
            newState.form.timeTo = enddate != null ? enddate.format("HH:mm") : null;

            startdate && startdate.startOf("day");
            enddate && enddate.startOf("day");

            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { history, onClose } = this.props;
            history.goBack();
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Speichert die vorgenommenen Änderungen an einer geladenen Sperre oder erstellt eine neue.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { isSeries } = this.props;
            const { mode, block, form } = this.state;

            var newLock: modelsPost.ILock = {};

            if (mode === Mode.create)
                newLock.calendar_ids = (form.calendarIds && form.calendarIds.length > 0) ? form.calendarIds.select(id => parseInt(id)) : null;
            else
                newLock.calendar_ids = (form.calendar && form.calendar.selectedValue > 0) ? [form.calendar.selectedValue] : null;

            newLock.starttime = form.startdate && form.startdate.format();

            if (isSeries)
                newLock.endtime = form.enddate && form.enddate.format();

            if (!form.isAllDay)
            {
                newLock.time_from = form.timeFrom;
                newLock.time_to = form.timeTo;
            }

            newLock.description = form.description;
            newLock.is_all_day = form.isAllDay;
            newLock.is_series = isSeries;

            if (mode === Mode.create)
                await apiTimes.postLock(newLock);
            else
                await apiTimes.putLock(block.id, newLock);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            if (e != null && e.responseJSON == null)
                console.error(e);

            if (e != null && e.responseJSON != null)
            {
                var errors = e.responseJSON;
                var newErrors = {};
                var alertMessage: string;

                for (let error in errors)
                {
                    if (errors[error] != null && errors[error].length > 0)
                        newErrors[error] = errors[error].first();
                }

                for (let error in newErrors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                        case "internal_server_error":
                            alertMessage = newErrors[error];
                            break;
                    }
                }

                this.setState(() =>
                {
                    const newState: IState = {};
                    newState.isLoading = false;
                    newState.errors = newErrors;
                    newState.alertMessage = alertMessage;
                    newState.alertType = AlertType.error;
                    return newState;
                });
            }
        }
    }

    /**
     * Löscht die geöffnete Sperre.
     */
    private onDeleteClick = async () =>
    {
        var deleteLock = confirm("Wollen Sie die Sperre wirklich löschen?");
        if (!deleteLock)
            return;
        try
        {
            this.setState({
                isLoading: true
            });

            const { block } = this.state;

            await apiTimes.deleteLock(block.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            if (e != null && e.responseJSON == null)
                console.error(e);

            if (e != null && e.responseJSON != null)
            {
                var errors = e.responseJSON;
                var newErrors = {};
                var alertMessage: string;

                for (let error in errors)
                {
                    if (errors[error] != null && errors[error].length > 0)
                        newErrors[error] = errors[error].first();
                }

                for (let error in newErrors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = newErrors[error];
                            break;
                    }
                }

                this.setState(() =>
                {
                    const newState: IState = {};
                    newState.isLoading = false;
                    newState.errors = newErrors;
                    newState.alertMessage = alertMessage;
                    newState.alertType = AlertType.error;
                    return newState;
                });
            }
        }
    }

    private onCalendarsChanged = (e: React.ChangeEvent<HTMLSelectElement>) =>
    {
        var target = e.target;
        var value: string[] = [];

        if (target.selectedOptions.length > 0)
        {
            for (var i = 0; i < target.selectedOptions.length; i++)
            {
                var selectedVal: string = target.selectedOptions.item(i).value;
                if (selectedVal !== "--")
                {
                    value.push(target.selectedOptions.item(i).value);
                }
            }
        }

        this.setState((state: IState) =>
        {
            state.form.calendarIds = value;
            return state;
        });
    }

    private onSelectionChanged = (selectedItem: any, formKey: string, valueKey: string, selectedValueKey: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[formKey] = {
                value: selectedItem ? selectedItem[valueKey] : null,
                selectedValue: selectedItem ? selectedItem[selectedValueKey] : null
            };
            return state;
        });
    }


    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines der Datums Controls ändert.
     * @param date Das neue Datum welches gesetzt werden soll.
     * @param name Der Name des Form Elements.
     */
    private onDateChanged = (date: moment.Moment, name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = date;
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form[name] = value;
            return state;
        });
    }

    private renderForm(): JSX.Element
    {
        const { isSeries } = this.props;
        const { mode, form, errors } = this.state;

        return (
            <div>
                <GroupBox>
                    <div className={`lock-dialog_content_wrapper ${mode === Mode.create ? "create" : "edit"}`}>
                        <div className="lock-dialog_content_left">
                            {
                                mode === Mode.create &&
                                <div
                                    className="input-group"
                                    style={{
                                        height: "100%"
                                    }}>
                                    <label htmlFor="calendarIds">Kalender</label>
                                    <select
                                        style={{
                                            height: "calc(100% - 21px)",
                                            resize: "none"
                                        }}
                                        id="calendarIds"
                                        name="calendarIds"
                                        className="input input--multiselect"
                                        multiple={true}
                                        value={form.calendarIds}
                                        onChange={this.onCalendarsChanged}>
                                        {this.renderOptions("id", "name", this.state.calendars)}></select>
                                </div>
                            }
                        </div>
                        <div className="lock-dialog_content_right">
                            <FormGroup>
                                {
                                    mode === Mode.edit &&
                                    <FormGroup>
                                        <ComboBox
                                            canRemoveSelection={false}
                                            label="Kalender"
                                            dataSource={this.state.calendars}
                                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                            comboBoxType={ComboBoxType.dropDownList}
                                            value={form.calendar.value}
                                            selectedValue={form.calendar.selectedValue}
                                            onSelectionChanged={(item) => this.onSelectionChanged(item, "calendar", "name", "id")}
                                            valueMember="id"
                                            displayMember="name"
                                            errorMessage={errors["CalendarId"]}>
                                            {
                                                (item: modelsGet.ICalendar) =>
                                                {
                                                    return (
                                                        <DropDownTextItem
                                                            key={item.id}
                                                            value={item.id}
                                                            displayValue={item.name} />
                                                    );
                                                }
                                            }
                                        </ComboBox>
                                    </FormGroup>
                                }
                                <DateTextBox
                                    label="Startdatum"
                                    date={form.startdate}
                                    alignPopup={PopupAlign.targetNodeBottomRight}
                                    onDateChange={(date) => this.onDateChanged(date, "startdate")}
                                    errorMessage={errors["Starttime"]} />
                                {
                                    isSeries &&
                                    <DateTextBox
                                        label="Endedatum"
                                        date={form.enddate}
                                        alignPopup={PopupAlign.targetNodeBottomRight}
                                        onDateChange={(date) => this.onDateChanged(date, "enddate")}
                                        errorMessage={errors["Endtime"]} />
                                }
                                <TextBox
                                    label="Beginn"
                                    mask={InputMask.time}
                                    size={InputSize.small}
                                    value={form.timeFrom}
                                    isDisabled={form.isAllDay}
                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "timeFrom")}
                                    errorMessage={errors["TimeFrom"]} />
                                <TextBox
                                    style={{
                                        marginBottom: 10
                                    }}
                                    label="Ende"
                                    mask={InputMask.time}
                                    size={InputSize.small}
                                    value={form.timeTo}
                                    isDisabled={form.isAllDay}
                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "timeTo")}
                                    errorMessage={errors["TimeTo"]} />
                                <CheckBox
                                    label="Ganztägig"
                                    isChecked={form.isAllDay}
                                    onCheckedChanged={(e) => this.onCheckboxChanged(e, "isAllDay")} />
                            </FormGroup>
                        </div>
                    </div>
                    <FormGroup style={{
                        marginTop: 20
                    }}>
                        <TextArea
                            rows={10}
                            placeholder="Geben Sie den Grund für die Sperre an."
                            value={form.description}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "description")}
                            errorMessage={errors["Description"]} />
                    </FormGroup>
                </GroupBox>
                {/*
                    isSeries &&
                    <GroupBox headerText="Serienmuster">
                        <FormGroup>
                            <ComboBox
                                canRemoveSelection={false}
                                label="Interval"
                                dataSource={this.state.seriesPatterns}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                value={form.seriesPattern.value}
                                selectedValue={form.seriesPattern.selectedValue}
                                onSelectionChanged={(item) => this.onSelectionChanged(item, "seriesPattern", "pattern", "id")}
                                valueMember="id"
                                displayMember="pattern"
                                errorMessage={errors["Interval"]}>
                                {
                                    (item: ISeriesPattern) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.pattern} />
                                        );
                                    }
                                }
                            </ComboBox>
                        </FormGroup>
                    </GroupBox>
                */}
            </div>
        );
    }

    /**
     * Erstellt die Auswahlmöglichkeiten für eine ComboBox.
     * @param value Der Name der Eigenschaft die den Wert der Option wiedergibt.
     * @param text Der Name der Eigenschaft die den Anzeigetext wiedergibt.
     * @param options Die Optionen anhand dessen Die Auswahlmöglichkeiten generiert werden sollen.
     */
    private renderOptions(value: string, text: string, options: any[])
    {
        var returnValue: any[] = options.map((option) =>
            <option value={option[value]}>{option[text]}</option>
        );

        return returnValue;
    }

    private renderContent(): JSX.Element
    {
        const { isSeries } = this.props;

        return (
            <div className="lock-dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    {this.renderForm()}
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isSeries } = this.props;
        const {
            isLoading,
            mode,
            selectedObject,
            selectedCalendar,
            block,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "block-helper";
                title = isSeries ? "Sperrserie erstellen" : "Sperre erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = block.calendar_name + ": Sperre " + block.id;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}>
                <div className="lock-dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="lock-dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}